import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { Currency, Price, WETH9 } from '@uniswap/sdk-core'
import { FeeAmount } from '@uniswap/v3-sdk'
import axios from 'axios'
import Loader from 'components/Loader'
import { API_POOL_BASE_URL } from 'constants/links'
import { useActiveWeb3React } from 'hooks'
import { useV3Factory } from 'hooks/useContract'
import { PresetPriceRange } from 'pages/AddLiquidity'
import { ChartHeader, ChartHeaderTitle, ChartTitle } from 'pages/ExplorePools/Metrics/styled'
import React from 'react'
import { Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { wrappedCurrency } from 'utils/wrappedCurrency'

const ContainerStyle = styled.div`
  cursor: default;
  width: 100%;
  height: 226px;
  max-height: 250px;
  padding: 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg1};
  overflow: hidden;

  .recharts-surface {
    overflow: visible;
  }
`

interface ChartRangeProps {
  currencyIdA: string | undefined
  currencyIdB: string | undefined
  tokenASymbol: string | null
  tokenBSymbol: string | null
  feeAmount: FeeAmount | undefined
  currencyA: Currency
  currencyB: Currency
  price: any
  priceLower?: Price
  priceUpper?: Price
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  presetPriceRange: PresetPriceRange
  invertedFromAPI: boolean
  setInvertedFromAPI: (value: boolean) => void
}

function isInverted(uiCurrentPrice: number, price: number, invertedPrice: number): boolean {
  const diffA = Math.abs(uiCurrentPrice - price)
  const diffB = Math.abs(uiCurrentPrice - invertedPrice)

  return diffA < diffB ? false : true
}

const ChartRange: React.FC<ChartRangeProps> = ({
  currencyIdA,
  currencyIdB,
  feeAmount,
  tokenASymbol,
  tokenBSymbol,
  currencyA,
  currencyB,
  price,
  priceLower,
  priceUpper,
  onLeftRangeInput,
  onRightRangeInput,
  presetPriceRange,
  setInvertedFromAPI,
  invertedFromAPI,
}) => {
  const { library, chainId } = useActiveWeb3React()

  const factoryContract = useV3Factory()
  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState<any[]>([])
  const [pairAddress, setPairAddress] = React.useState<null | string>(null)
  const tokenA = wrappedCurrency(currencyA, chainId)
  const tokenB = wrappedCurrency(currencyB, chainId)
  const chartRef = React.useRef<any>(null)
  console.log(`Pair address: ${pairAddress}`)
  const isSorted = tokenA && tokenB && tokenA.address !== tokenB.address && tokenA.sortsBefore(tokenB)
  const chartTokenBaseName = currencyB.symbol + '/' + currencyA.symbol

  // Track previous input values
  const previousInputs = React.useRef<{
    currencyIdA?: string
    currencyIdB?: string
    price?: any
    feeAmount?: FeeAmount
  }>({})

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current?.setState({})
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  React.useEffect(() => {
    // dirty isInversed logic
    const dataArray = data?.[0]?.data
    console.log('_____')
    console.log('Starting function')
    if (dataArray?.length > 0) {
      const dataWithoutEmpty = dataArray.filter((item: any[]) => item[4] && item[4] > 0)
      const middleValue = dataWithoutEmpty[Math.round(dataWithoutEmpty.length / 2)]
      if (middleValue) {
        console.log('middleValue')
        const priceAPI = middleValue[4]
        const invertedPrice = 1 / middleValue[4]
        console.log('priceAPI', priceAPI)
        console.log('invertedPrice', invertedPrice)
        console.log('uiPrice', price)
        if (price && invertedPrice && priceAPI) {
          const hasToInvert = isInverted(Number(price), priceAPI, invertedPrice)
          console.log('hasToInvert', hasToInvert)
          setInvertedFromAPI(hasToInvert)
        } else {
          setInvertedFromAPI(false)
        }
      } else {
        setInvertedFromAPI(false)
      }
    }
  }, [data, price, setInvertedFromAPI])

  React.useEffect(() => {
    const fetchData = async () => {
      if (!factoryContract || !currencyIdA || !currencyIdB || !feeAmount || !library) {
        return
      }

      const tokenAAddress = currencyIdA === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdA
      const tokenBAddress = currencyIdB === 'HYPE' ? WETH9[CURRENT_CHAIN].address : currencyIdB

      try {
        setLoading(true)

        const pairAddress = await factoryContract.getPool(tokenAAddress, tokenBAddress, feeAmount)
        setPairAddress(pairAddress)
        /* const poolContract = new Contract(
          pairAddress,
          ['function token0() view returns (address)', 'function token1() view returns (address)'],
          library
        )

        const token1 = await poolContract.token1() */

        const response = await axios.post(`${API_POOL_BASE_URL}/api/batch-candles`, {
          candleInputs: [
            {
              pair: chartTokenBaseName,
              interval: '15m',
              exchange: pairAddress.toLowerCase(),
              limit: 300,
            },
          ],
        })

        setData(response?.data?.data ? response?.data?.data : [])
        setLoading(false)
      } catch (e) {
        console.warn(e)
        setData([])
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }

    const sameAsPrevious =
      previousInputs.current.currencyIdA === currencyIdA &&
      previousInputs.current.currencyIdB === currencyIdB &&
      previousInputs.current.feeAmount === feeAmount &&
      previousInputs.current.price === price

    if (sameAsPrevious) return
    previousInputs.current = { currencyIdA, currencyIdB, feeAmount }

    if (!sameAsPrevious) {
      fetchData()
    }
  }, [
    factoryContract,
    currencyIdA,
    currencyIdB,
    feeAmount,
    library,
    tokenASymbol,
    tokenBSymbol,
    chartTokenBaseName,
    price,
    setInvertedFromAPI,
  ])

  const priceData = data?.[0]?.data

  const shouldInvert = invertedFromAPI

  // Hard
  const formattedData = priceData
    ?.filter((item: any[]) => item[4] && item[4] > 0)
    .map((item: any[]) => ({
      timestamp: item[0] * 1000,
      close: shouldInvert ? 1 / item[4] : item[4],
    }))

  const latestClose = formattedData?.[formattedData.length - 1]?.close
  const closePrices = formattedData?.map((d: any) => d.close) ?? []
  const minPrice = Math.min(...closePrices)
  const maxPrice = Math.max(...closePrices)

  const distanceToMin = Math.abs(latestClose - minPrice)
  const distanceToMax = Math.abs(maxPrice - latestClose)
  const maxDistance = Math.max(distanceToMin, distanceToMax)

  const isValidNumber = (n: any) => typeof n === 'number' && !isNaN(n)

  const yDomain =
    isValidNumber(latestClose) && isValidNumber(maxDistance)
      ? [latestClose - maxDistance, latestClose + maxDistance]
      : ['auto', 'auto']

  const priceLowerSorted = isSorted ? priceLower?.toSignificant() : priceUpper?.invert()?.toSignificant()
  const priceUpperSorted = isSorted ? priceUpper?.toSignificant() : priceLower?.invert()?.toSignificant()
  const yScale = (value: number) => {
    const chart = chartRef.current
    const scale = chart?.state?.yAxisMap?.['0']?.scale
    return typeof scale === 'function' ? scale(value) : 0
  }

  const startDrag = (e: React.MouseEvent<SVGRectElement, MouseEvent>, type: 'min' | 'max') => {
    const svg = e.currentTarget.ownerSVGElement
    if (!svg) return

    const onMouseMove = (moveEvent: MouseEvent) => {
      const svgRect = svg.getBoundingClientRect()
      const y = moveEvent.clientY - svgRect.top
      const chartHeight = svgRect.height
      const pctFromTop = y / chartHeight
      const value = yDomain[1] - pctFromTop * (yDomain[1] - yDomain[0])

      if (type === 'min') {
        onLeftRangeInput(value.toFixed(6))
      } else {
        onRightRangeInput(value.toFixed(6))
      }
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  return (
    <>
      {loading === true ? (
        <>
          <ContainerStyle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader size="32px" />
          </ContainerStyle>
        </>
      ) : (
        <>
          {priceData?.length > 0 ? (
            <ContainerStyle>
              <ChartHeader>
                <ChartHeaderTitle>
                  <ChartTitle>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                      Price chart{' '}
                    </div>
                  </ChartTitle>
                </ChartHeaderTitle>
              </ChartHeader>
              <ResponsiveContainer width="100%" height={185}>
                <LineChart data={formattedData} ref={chartRef}>
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    domain={['auto', 'auto']}
                    tickFormatter={(time: number) => {
                      const date = new Date(time)
                      return date.toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    }}
                    tick={{ fontSize: 10, fill: '#C3C5CB' }}
                    minTickGap={35}
                  />
                  <YAxis
                    dataKey="close"
                    domain={yDomain}
                    tick={{ fontSize: 10, fill: '#C3C5CB' }}
                    orientation="right"
                    tickFormatter={(value) => value.toFixed(5)}
                  />
                  <ReferenceLine
                    y={latestClose}
                    stroke="#C3C5CB"
                    strokeDasharray="3 3"
                    ifOverflow="extendDomain"
                    label={<LastPriceLabel value={price} />}
                  />

                  {priceLowerSorted && priceUpperSorted && presetPriceRange === null && (
                    <>
                      {/* Drag icon below lower price line */}
                      <foreignObject
                        x="calc(50% - 50px)"
                        y={yScale(parseFloat(priceLowerSorted)) + 6}
                        width="30"
                        height="12"
                        style={{ pointerEvents: 'none' }}
                      >
                        <div
                          style={{
                            width: '30px',
                            height: '4px',
                            backgroundColor: '#AAA',
                            borderRadius: '2px',
                            margin: 'auto',
                          }}
                        />
                      </foreignObject>

                      {/* Lower price line */}
                      <ReferenceLine
                        y={parseFloat(priceLowerSorted)}
                        stroke="#AAA"
                        strokeDasharray="1 1"
                        ifOverflow="extendDomain"
                        isFront
                        label={{
                          value: '↑',
                          position: 'left',
                          fontSize: 10,
                          fill: 'white',
                        }}
                      />
                      <rect
                        x={0}
                        y={yScale(parseFloat(priceLowerSorted)) - 16}
                        width="100%"
                        height={32}
                        fill="transparent"
                        cursor="ns-resize"
                        style={{ pointerEvents: 'all' }}
                        onMouseDown={(e) => startDrag(e, 'min')}
                      />

                      {/* Drag icon above upper price line */}
                      <foreignObject
                        x="calc(50% - 50px)"
                        y={yScale(parseFloat(priceUpperSorted)) - 10}
                        width="30"
                        height="12"
                        style={{ pointerEvents: 'none' }}
                      >
                        <div
                          style={{
                            width: '30px',
                            height: '4px',
                            backgroundColor: '#AAA',
                            borderRadius: '2px',
                            margin: 'auto',
                          }}
                        />
                      </foreignObject>

                      {/* Upper price line */}
                      <ReferenceLine
                        y={parseFloat(priceUpperSorted)}
                        stroke="#AAA"
                        strokeDasharray="1 1"
                        ifOverflow="extendDomain"
                        isFront
                        label={{
                          value: '↓',
                          position: 'left',
                          fontSize: 10,
                          fill: 'white',
                        }}
                      />
                      <rect
                        x={0}
                        y={yScale(parseFloat(priceUpperSorted)) - 16}
                        width="100%"
                        height={32}
                        fill="transparent"
                        cursor="ns-resize"
                        onMouseDown={(e) => startDrag(e, 'max')}
                      />

                      {/* Highlight range */}
                      <ReferenceArea
                        y1={parseFloat(priceLowerSorted)}
                        y2={parseFloat(priceUpperSorted)}
                        stroke="none"
                        fill="#4afb3920"
                        ifOverflow="extendDomain"
                      />
                    </>
                  )}

                  {presetPriceRange === 'maxRange' && isValidNumber(yDomain[0]) && isValidNumber(yDomain[1]) && (
                    <>
                      <ReferenceArea
                        y1={yDomain[0]}
                        y2={yDomain[1]}
                        stroke="none"
                        fill="#4afb3910"
                        ifOverflow="extendDomain"
                      />
                      <ReferenceLine
                        y={yDomain[0]}
                        stroke="#4AFB39"
                        strokeDasharray="1 1"
                        ifOverflow="extendDomain"
                        label={<InfinitePriceLabel value={'≈ 0'} />}
                      />
                      <ReferenceLine
                        y={yDomain[1]}
                        stroke="#4AFB39"
                        strokeDasharray="1 1"
                        ifOverflow="extendDomain"
                        label={<InfinitePriceLabel value={'∞'} />}
                      />
                    </>
                  )}
                  {presetPriceRange !== null &&
                    presetPriceRange !== 'maxRange' &&
                    priceLowerSorted &&
                    priceUpperSorted && (
                      <>
                        <ReferenceLine
                          y={parseFloat(priceLowerSorted)}
                          stroke="#4AFB39"
                          strokeDasharray="1 1"
                          ifOverflow="extendDomain"
                          isFront
                          label={{
                            value: '↑',
                            position: 'left',
                            fontSize: 10,
                            fill: 'white',
                          }}
                        />
                        <ReferenceLine
                          y={parseFloat(priceUpperSorted)}
                          stroke="#4AFB39"
                          strokeDasharray="1 1"
                          ifOverflow="extendDomain"
                          isFront
                          label={{
                            value: '↓',
                            position: 'left',
                            fontSize: 10,
                            fill: 'white',
                          }}
                        />
                        <ReferenceArea
                          y1={parseFloat(priceLowerSorted)}
                          y2={parseFloat(priceUpperSorted)}
                          stroke="none"
                          fill="#4afb3920"
                          ifOverflow="extendDomain"
                        />
                      </>
                    )}
                  <Line type="monotone" dataKey="close" stroke="#4AFB39A1" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ContainerStyle>
          ) : (
            <></>
          )}
        </>
      )}
    </>
  )
}
export default ChartRange

const LastPriceLabel: React.FC<{ value?: string | number; viewBox?: any }> = ({ value, viewBox }) => {
  const { y } = viewBox
  const rectHeight = 18
  const padding = 10
  const charWidth = 5
  const rectWidth = value ? String(value).length * charWidth + padding * 2 : 50
  const x = 10

  if (y && value) {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect x={0} y={-rectHeight / 2} width={rectWidth} height={rectHeight} rx={6} ry={6} fill="#4AFB39" />
        <text x={rectWidth / 2} y={4} textAnchor="middle" fill="black" fontSize="10">
          {value}
        </text>
      </g>
    )
  } else return <></>
}

const InfinitePriceLabel: React.FC<{ value?: string | number; viewBox?: any }> = ({ value, viewBox }) => {
  const { y } = viewBox
  const rectHeight = 16
  const padding = 10
  const charWidth = 2
  const rectWidth = value ? String(value).length * charWidth + padding * 2 : 50
  const x = 10

  if (value && viewBox) {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect x={0} y={-rectHeight / 2} width={rectWidth} height={rectHeight} rx={6} ry={6} fill="#4AFB39" />
        <text x={rectWidth / 2} y={4} textAnchor="middle" fill="black" fontSize="10">
          {value}
        </text>
      </g>
    )
  } else {
    return <></>
  }
}
